import { useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function ReadPage() {
  const { id, chapterNum } = useParams<{ id: string; chapterNum?: string }>();
  const comic = getComicById(Number(id));
  const chapterNumber = Number(chapterNum) || 1;

  if (!comic) {
    return <NotFound />;
  }

  const categoryInfo = categories.find((c) => c.slug === comic.category);
  const currentChapter = comic.chapters.find((ch) => ch.number === chapterNumber) || comic.chapters[0];

  return (
    <Layout>
      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryInfo?.name || '漫画', href: `/category/${comic.category}` },
            { label: comic.title, href: `/article/${comic.id}` },
            { label: currentChapter?.title || `第${chapterNumber}话` },
          ]}
        />

        <section className="max-w-3xl mx-auto px-4 md:px-6 py-8">
          {/* Chapter Navigation */}
          <div className="flex items-center justify-between mb-6">
            <a
              href={`/article/${comic.id}`}
              className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回详情
            </a>
            <h1 className="text-base font-bold font-heading text-foreground-950">
              {comic.title} - {currentChapter?.title || `第${chapterNumber}话`}
            </h1>
            <div className="flex items-center gap-2">
              {comic.chapters.find((ch) => ch.number === chapterNumber - 1) && (
                <a
                  href={`/article/${comic.id}/read/${chapterNumber - 1}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="px-3 py-1.5 text-xs font-medium bg-background-100 text-foreground-600 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap"
                >
                  上一话
                </a>
              )}
              {comic.chapters.find((ch) => ch.number === chapterNumber + 1) && (
                <a
                  href={`/article/${comic.id}/read/${chapterNumber + 1}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="px-3 py-1.5 text-xs font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  下一话
                </a>
              )}
            </div>
          </div>

          {/* Reading Area */}
          <div className="bg-background-100 rounded-xl p-8 md:p-12 min-h-[600px] flex flex-col items-center justify-center text-center">
            <div className="w-24 h-24 mb-6 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-book-read-line text-3xl text-primary-500"></i>
            </div>
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-3">
              {currentChapter?.title || `第${chapterNumber}话`}
            </h2>
            <p className="text-sm text-foreground-500 max-w-md leading-relaxed mb-4">
              这是{comic.title}的第{chapterNumber}话内容。<br />
              韩漫画网为您提供<strong>下拉式免费阅读</strong>体验，所有章节均可免费浏览。
            </p>
            <div className="flex items-center gap-2 text-xs text-foreground-400">
              <span>作者：{comic.author}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span>{comic.status}</span>
            </div>
          </div>

          {/* Bottom Navigation */}
          <div className="flex items-center justify-between mt-6">
            {comic.chapters.find((ch) => ch.number === chapterNumber - 1) ? (
              <a
                href={`/article/${comic.id}/read/${chapterNumber - 1}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-200 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                上一话
              </a>
            ) : (
              <div></div>
            )}
            <a
              href={`/article/${comic.id}`}
              className="flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full font-medium text-sm hover:bg-background-200 transition-colors"
            >
              <i className="ri-list-unordered"></i>
              章节目录
            </a>
            {comic.chapters.find((ch) => ch.number === chapterNumber + 1) ? (
              <a
                href={`/article/${comic.id}/read/${chapterNumber + 1}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors"
              >
                下一话
                <i className="ri-arrow-right-line"></i>
              </a>
            ) : (
              <div></div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}